import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Clock3,
  Eye,
  FileText,
  MessageSquare,
  Maximize2,
  Paperclip,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import {TimelineDialog} from "./timeline-dialog";
import { api, CatalogItem, Maintenance, MaintenanceItem, Occurrence, OccurrenceKanbanTemplate } from "./api";
import { KanbanBoard, type KanbanColumn } from "./kanban-board";
import { UppercaseInput, UppercaseTextarea } from "./uppercase-fields";
import {
  MultiSelectFilter,
  TableDisplayControls,
  useTablePreferences,
} from "./table-controls";
import { DatePicker } from "./date-picker";
import { leadTimeDays, leadTimeTone } from "./lead-time";
import { AttachmentFullPreview, AttachmentThumbnail } from "./attachment-thumbnail";
import { RichMarkdownEditor } from "./rich-markdown-editor";
import { resolveKanbanModelColumn } from "./kanban-model";
import {ATTACHMENT_ACCEPT,attachmentSelectionError} from "./attachment-policy";
import {RecordTrashAction} from "./record-trash-action";
import "./triage-opinion-preview.css";

const statusLabel: Record<string, string> = {
  draft: "Rascunho",
  awaiting_triage: "Aguardando triagem",
  needs_information: "Mais informações",
  in_analysis: "Em análise",
  in_treatment: "Em tratamento",
  awaiting_evidence: "Aguardando evidência",
  awaiting_effectiveness: "Aguardando eficácia",
  completed: "Concluída",
  cancelled: "Cancelada",
  not_applicable: "Não procedente",
  reopened: "Reaberta",
};
const occurrenceStatusAliases: Record<string, string[]> = {
  draft: ["awaiting_triage"],
  needs_information: ["awaiting_triage"],
  awaiting_evidence: ["in_treatment"],
  awaiting_effectiveness: ["in_treatment"],
  reopened: ["in_treatment"],
  not_applicable: ["cancelled"],
};
const tone = (status: string) =>
  status === "completed"
    ? "success"
    : status === "awaiting_triage" || status === "awaiting_evidence"
      ? "warning"
      : status === "cancelled" || status === "not_applicable"
        ? "danger"
        : "info";
const timelineColor = (status?: string) => {
  const statusTone = tone(String(status || ""));
  return statusTone === "success"
    ? "#16a34a"
    : statusTone === "warning"
      ? "#f59e0b"
      : statusTone === "danger"
        ? "#ef4444"
        : "#4f46e5";
};
const occurrenceColumns = [
  { key: "code", label: "Código", required: true },
  { key: "occurrence", label: "Ocorrência" },
  { key: "date", label: "Data" },
  { key: "notifier", label: "Setor notificador" },
  { key: "category", label: "Categoria" },
  { key: "owner", label: "Responsável" },
  { key: "due", label: "Prazo" },
  { key: "status", label: "Status" },
];
type View = { kind: "list" } | { kind: "new" } | { kind: "detail"; id: string };
type OccurrenceEvidence={id:string;file_name:string;mime_type:string;size:number;description?:string;uploaded_at?:string};

function EvidenceThumbnail({occurrenceId,evidence}:{occurrenceId:string;evidence:OccurrenceEvidence}){
  const [file,setFile]=useState<File>();
  useEffect(()=>{let active=true;void api.occurrenceEvidenceContent(occurrenceId,evidence.id).then(blob=>{if(active)setFile(new File([blob],evidence.file_name,{type:evidence.mime_type}))}).catch(()=>undefined);return()=>{active=false}},[occurrenceId,evidence.id,evidence.file_name,evidence.mime_type]);
  return file?<AttachmentThumbnail file={file}/>:<div className="attachment-thumbnail loading" aria-label="Carregando miniatura"><span/></div>;
}

export function OccurrencesModule({
  onError,
  listRequest,
}: {
  onError: (message: string) => void;
  listRequest: number;
}) {
  const [view, setView] = useState<View>({ kind: "list" });
  const previousListRequest=useRef(listRequest);
  useEffect(() => { if(listRequest===previousListRequest.current)return;previousListRequest.current=listRequest;setView({ kind: "list" }); }, [listRequest]);
  if (view.kind === "new")
    return (
      <OccurrenceForm
        onCancel={() => setView({ kind: "list" })}
        onSaved={(id) => setView({ kind: "detail", id })}
        onError={onError}
      />
    );
  if (view.kind === "detail")
    return (
      <OccurrenceDetail
        id={view.id}
        onBack={() => setView({ kind: "list" })}
        onError={onError}
      />
    );
  return (
    <OccurrenceList
      onOpen={(id) => setView({ kind: "detail", id })}
      onAdd={() => setView({ kind: "new" })}
      onError={onError}
    />
  );
}

function OccurrenceList({
  onOpen,
  onAdd,
  onError,
}: {
  onOpen: (id: string) => void;
  onAdd: () => void;
  onError: (x: string) => void;
}) {
  const [rows, setRows] = useState<Occurrence[]>([]),
    [q, setQ] = useState(""),
    [statuses, setStatuses] = useState<string[]>([]),
    [categories, setCategories] = useState<string[]>([]),
    [page, setPage] = useState(1),
    [apiPages, setApiPages] = useState(1),
    [total, setTotal] = useState(0),
    [templates,setTemplates]=useState<OccurrenceKanbanTemplate[]>([]),
    [templateId,setTemplateId]=useState("");
  const preferences = useTablePreferences(
    "sgq:occurrences-table",
    occurrenceColumns,
  );
  const load = () =>
    Promise.all([
      api.occurrences(q, "", preferences.view==="kanban"?1:page, preferences.view==="kanban"?5000:10, statuses.join(",")),
      api.occurrenceKanbanTemplates(),
    ])
      .then(([result, nextTemplates]) => {
        setRows(result.items);
        setTotal(result.total);
        setApiPages(result.pages);
        setTemplates(nextTemplates);
        setTemplateId(current=>current&&nextTemplates.some(template=>template.id===current)?current:nextTemplates[0]?.id||"");
      })
      .catch((e) => onError(e.message));
  useEffect(() => {
    void load();
  }, [q, statuses, page, preferences.view]);
  useEffect(()=>setPage(1),[preferences.view]);
  useEffect(()=>{setStatuses([]);setPage(1)},[templateId]);
  const activeTemplate=templates.find(template=>template.id===templateId)||templates[0];
  const kanbanColumns:KanbanColumn[]=(activeTemplate?.columns||[]).map(column=>({key:column.id,label:column.name,statuses:[column.id],color:column.color}));
  const modelColumnForRow=(row:Occurrence)=>resolveKanbanModelColumn(activeTemplate,row,occurrenceStatusAliases);
  const columnForRow=(row:Occurrence)=>modelColumnForRow(row)?.id||"";
  const categoryOptions = Array.from(
    new Set(rows.map((row) => row.category_name || "Não classificada")),
  ).map((value) => ({ value, label: value }));
  const filteredRows = rows.filter(
    (row) =>
      !categories.length ||
      categories.includes(row.category_name || "Não classificada"),
  );
  const pages=apiPages;
  const visibleRows=filteredRows;
  useEffect(()=>setPage(current=>Math.min(current,pages)),[pages]);
  return (
    <div className="occurrence-index">
      <section
        className={`card table-card data-table-card occurrence-table ${!visibleRows.length && preferences.view === "table" ? "empty-table-card" : ""} ${preferences.className}`}
      >
        <div className="toolbar occurrence-toolbar table-toolbar">
          <div className="table-filter-group">
            <label className="search">
              <Search />
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder="Buscar código, título, fato, setor ou responsável"
              />
            </label>
            <MultiSelectFilter
              label="Todos os status"
              options={(activeTemplate?.columns||[]).map(column => ({
                value: column.id,
                label: column.name,
              }))}
              value={statuses}
              onChange={(value) => {
                setStatuses(value);
                setPage(1);
              }}
            />
            {templates.length > 1 && <label className="kanban-template-select"><span>Modelo</span><select value={activeTemplate?.id||""} onChange={event=>setTemplateId(event.target.value)}>{templates.map(template=><option key={template.id} value={template.id}>{template.name}</option>)}</select></label>}
            <MultiSelectFilter
              label="Todas as categorias"
              options={categoryOptions}
              value={categories}
              onChange={setCategories}
            />
          </div>
          <div className="table-toolbar-actions">
            <TableDisplayControls
              count={visibleRows.length}
              total={total}
              columns={occurrenceColumns}
              hidden={preferences.hidden}
              view={preferences.view}
              onView={preferences.setView}
              onToggle={preferences.toggle}
              onReset={preferences.reset}
            />
            <button className="button primary" onClick={onAdd}><Plus />Nova NC</button>
          </div>
        </div>
        {preferences.view === "kanban" ? (
          <KanbanBoard
            columns={kanbanColumns}
            rows={visibleRows}
            getStatus={columnForRow}
            onOpen={(row) => onOpen(row.id)}
            onStatusChange={async(row,columnId,comment)=>{try{await api.changeOccurrenceKanbanStatus(row.id,columnId,comment);await load()}catch(error:any){onError(error.message);throw error}}}
            renderCard={(row) => {
              const finished=row.lead_time_stopped_at||row.closed_at||(["completed","cancelled","not_applicable"].includes(row.status)?row.updated_at:undefined);
              const days=leadTimeDays(row.created_at,finished);
              return <>
                <strong>{row.title}</strong>
                <div className="kanban-card-footer"><small>{row.code}</small><span className={`lead-time lead-time-${leadTimeTone(days)}`} title={`Lead time: ${days} dia${days===1?"":"s"}`}><Clock3/>{days}d</span></div>
              </>;
            }}
          />
        ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th data-column="code">Código</th>
                <th data-column="occurrence">Ocorrência</th>
                <th data-column="date">Data</th>
                <th data-column="notifier">Setor notificador</th>
                <th data-column="category">Categoria</th>
                <th data-column="owner">Responsável</th>
                <th data-column="due">Prazo</th>
                <th data-column="status">Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const modelColumn=modelColumnForRow(row);
                return (
                <tr
                  key={row.id}
                  className="clickable-row"
                  tabIndex={0}
                  onClick={() => onOpen(row.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpen(row.id);
                    }
                  }}
                >
                  <td data-column="code">{row.code}</td>
                  <td data-column="occurrence">
                    <strong>{row.title}</strong>
                  </td>
                  <td data-column="date">
                    {new Date(`${row.occurrence_date}T12:00:00`).toLocaleDateString("pt-BR")}
                  </td>
                  <td data-column="notifier">{row.notifier_department}</td>
                  <td data-column="category">
                    {row.category_name || "Não classificada"}
                  </td>
                  <td data-column="owner">{row.assignee || "Não atribuído"}</td>
                  <td data-column="due">
                    {row.due_date
                      ? new Date(`${row.due_date}T12:00:00`).toLocaleDateString(
                          "pt-BR",
                        )
                      : "—"}
                  </td>
                  <td data-column="status">
                    <span className="status-pill" style={{borderColor:modelColumn?.color||"",color:modelColumn?.color||"",backgroundColor:modelColumn?.color?`color-mix(in srgb,${modelColumn.color} 12%,transparent)`:undefined}}>
                      {modelColumn?.name || "Sem etapa configurada"}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        {!visibleRows.length && (
          <div className="empty">
            <FileText />
            <h3>Nenhuma ocorrência encontrada</h3>
            <p>Registre uma ocorrência ou ajuste os filtros.</p>
          </div>
        )}
        {preferences.view === "table" && <div className="pagination">
          <button
            disabled={page === 1}
            onClick={() => setPage((value) => value - 1)}
          >
            Anterior
          </button>
          <span>
            Página {page} de {pages}
          </span>
          <button
            disabled={page === pages}
            onClick={() => setPage((value) => value + 1)}
          >
            Próxima
          </button>
        </div>}
      </section>
    </div>
  );
}

function OccurrenceForm({
  onCancel,
  onSaved,
  onError,
}: {
  onCancel: () => void;
  onSaved: (id: string) => void;
  onError: (x: string) => void;
}) {
  const [catalogs, setCatalogs] = useState<Record<string, CatalogItem[]>>({}),
    [saving, setSaving] = useState(false);
  const [maintenancePickerOpen,setMaintenancePickerOpen]=useState(false),[maintenances,setMaintenances]=useState<Maintenance[]>([]),[maintenanceSearch,setMaintenanceSearch]=useState("");
  const [selectedMaintenance,setSelectedMaintenance]=useState<Maintenance>(),[maintenanceItems,setMaintenanceItems]=useState<MaintenanceItem[]>([]),[selectedInputIds,setSelectedInputIds]=useState<string[]>([]),[selectedItemIds,setSelectedItemIds]=useState<string[]>([]),[pendingWholeItem,setPendingWholeItem]=useState<MaintenanceItem>(),[loadingMaintenance,setLoadingMaintenance]=useState(false);
  useEffect(() => {
    api
      .occurrenceCatalogs()
      .then(setCatalogs)
      .catch((e) => onError(e.message));
  }, []);
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if(selectedMaintenance&&!selectedInputIds.length&&!selectedItemIds.length){
      onError("Selecione ao menos um insumo ou um item completo do PM para criar as não conformidades.");
      return;
    }
    setSaving(true);
    const f = new FormData(e.currentTarget);
    try {
      const payload={
        title: f.get("title"),
        fact: f.get("fact"),
        occurrenceDate: f.get("occurrenceDate"),
        notifierDepartmentId: f.get("notifierDepartmentId"),
        categoryId: f.get("categoryId") || undefined,
        originId: f.get("originId") || undefined,
        problemSource: f.get("problemSource") || undefined,
        typeId: f.get("typeId") || undefined,
        severity: f.get("priority"),
        priority: f.get("priority"),
        initialEvidence: f.get("initialEvidence"),
      };
      if(selectedMaintenance){
        const created=await api.createOccurrencesFromMaintenance({...payload,maintenanceId:selectedMaintenance.id,inputIds:selectedInputIds,itemIds:selectedItemIds});
        onSaved(created.items[0].id);
      }else{
        const created=await api.createOccurrence(payload);
        onSaved(created.id);
      }
    } catch (e: any) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form className="card form occurrence-form" onSubmit={submit}>
      <div className="form-heading">
        <div>
          <p className="section-kicker">Novo registro</p>
          <h2>Registrar ocorrência</h2>
          <p>O código e a autoria serão definidos automaticamente.</p>
        </div>
        <div className="form-heading-actions">
          <button type="button" className="button" onClick={()=>{setMaintenancePickerOpen(true);if(!maintenances.length)void api.list().then(setMaintenances).catch(error=>onError(error.message))}}>Selecionar PM</button>
        </div>
      </div>
      {selectedMaintenance&&<div className="maintenance-source-summary"><div><span className="section-kicker">PM relacionado</span><strong>{selectedMaintenance.code} · {selectedMaintenance.customer_name||"CLIENTE NÃO INFORMADO"}</strong><small>{selectedInputIds.length} insumo(s) e {selectedItemIds.length} item(ns) completo(s) selecionado(s)</small></div><button type="button" className="icon-button" aria-label="Remover vínculo com PM" onClick={()=>{setSelectedMaintenance(undefined);setMaintenanceItems([]);setSelectedInputIds([]);setSelectedItemIds([])}}><X/></button></div>}
      <div className="form-grid occurrence-create-grid">
        <Field label="Data da ocorrência" className="occurrence-third">
          <DatePicker
            name="occurrenceDate"
            defaultValue={new Date().toISOString().slice(0, 10)}
            required={!selectedMaintenance}
            aria-label="Data da ocorrência"
          />
        </Field>
        <Field label="Setor notificador" className="occurrence-third">
          <CatalogSelect
            name="notifierDepartmentId"
            items={catalogs.department}
            required
          />
        </Field>
        <Field label="Categoria" className="occurrence-third">
          <CatalogSelect
            name="categoryId"
            items={catalogs.category}
            placeholder="Classificar na triagem"
          />
        </Field>
        <Field label="Origem" className="occurrence-quarter">
          <CatalogSelect
            name="originId"
            items={catalogs.origin}
            placeholder="Classificar na triagem"
          />
        </Field>
        <Field label="Fonte" className="occurrence-quarter">
          <UppercaseInput name="problemSource" maxLength={500} placeholder="Informe a causa do problema" />
        </Field>
        <Field label="Tipo de ocorrência" className="occurrence-quarter">
          <CatalogSelect
            name="typeId"
            items={catalogs.type}
            placeholder="Classificar na triagem"
          />
        </Field>
        <Field label="Prioridade" className="occurrence-quarter">
          <select name="priority">
            <option value="low">Baixa</option>
            <option value="medium">Média</option>
            <option value="high">Alta</option>
            <option value="critical">Crítica</option>
          </select>
        </Field>
        <Field label="Título da ocorrência" full>
          <input
            name="title"
            maxLength={180}
            required
            placeholder="Resumo objetivo do ocorrido"
          />
        </Field>
        <Field label="Assunto, fato e evidência da ocorrência" full>
          <textarea
            name="fact"
            required
            placeholder="Descreva o que aconteceu, onde, quando e qual evidência foi observada"
          />
        </Field>
        <Field label="Evidência inicial textual" full>
          <textarea
            name="initialEvidence"
            placeholder="Referências, números de pedido, lote, equipamento ou observações iniciais"
          />
        </Field>
      </div>
      {maintenancePickerOpen&&<div className="dialog-backdrop" role="presentation" onMouseDown={()=>setMaintenancePickerOpen(false)}><section className="maintenance-picker-dialog" role="dialog" aria-modal="true" onMouseDown={event=>event.stopPropagation()}><header><div><span className="section-kicker">Criar a partir de PM</span><h2>Selecione os itens ou insumos que gerarão NCs</h2><p>Cada insumo ou item completo selecionado criará uma NC vinculada ao mesmo PM.</p></div><button type="button" className="icon-button" onClick={()=>setMaintenancePickerOpen(false)} aria-label="Fechar"><X/></button></header>{!selectedMaintenance?<><label className="focco-insumo-search"><Search/><input autoFocus value={maintenanceSearch} onChange={event=>setMaintenanceSearch(event.target.value)} placeholder="Buscar por código, cliente ou descrição"/></label><div className="maintenance-picker-list">{maintenances.filter(pm=>`${pm.code} ${pm.customer_name||""} ${pm.summary||""}`.toLocaleLowerCase("pt-BR").includes(maintenanceSearch.toLocaleLowerCase("pt-BR"))).map(pm=><button type="button" key={pm.id} onClick={()=>{setLoadingMaintenance(true);void api.maintenanceItemInputs(pm.id).then(items=>{setSelectedMaintenance(pm);setMaintenanceItems(items);setSelectedInputIds([]);setSelectedItemIds([])}).catch(error=>onError(error.message)).finally(()=>setLoadingMaintenance(false))}}><strong>{pm.code}</strong><span>{pm.customer_name||"CLIENTE NÃO INFORMADO"}</span><small>{pm.summary}</small></button>)}</div>{loadingMaintenance&&<p>Carregando itens e insumos…</p>}</>:<><button type="button" className="ghost-link" onClick={()=>{setSelectedMaintenance(undefined);setMaintenanceItems([]);setSelectedInputIds([]);setSelectedItemIds([])}}><ArrowLeft/>Escolher outro PM</button><div className="maintenance-input-tree"><h3>{selectedMaintenance.code}</h3>{maintenanceItems.map((item,index)=>{const itemInputIds=(item.inputs||[]).map(input=>input.id),hasSelectedInput=itemInputIds.some(id=>selectedInputIds.includes(id)),wholeItemSelected=selectedItemIds.includes(item.id);return <section key={item.id}><div className="maintenance-item-heading"><label className="maintenance-whole-item"><input type="checkbox" checked={wholeItemSelected} disabled={hasSelectedInput} onChange={event=>{if(event.target.checked)setPendingWholeItem(item);else setSelectedItemIds(current=>current.filter(id=>id!==item.id))}}/><span><strong>ITEM {index+1}: {item.description}</strong><small>{wholeItemSelected?"Item completo selecionado":hasSelectedInput?"Há insumos selecionados neste item":"Selecionar o item completo"}</small></span></label><small>Quantidade afetada: {item.affected_quantity}</small></div>{item.inputs?.length?<div className="maintenance-input-options">{item.inputs.map(input=><label key={input.id} className={wholeItemSelected?"is-disabled":undefined}><input type="checkbox" disabled={wholeItemSelected} checked={selectedInputIds.includes(input.id)} onChange={event=>setSelectedInputIds(current=>event.target.checked?[...current,input.id]:current.filter(id=>id!==input.id))}/><span className="maintenance-input-code">{input.input_code}</span><span className="maintenance-input-description">{input.description}</span><small className="maintenance-input-unit">Unidade {input.unit_number||1}</small><small className="maintenance-input-quantity">{input.quantity} {input.unit||""}</small></label>)}</div>:<p className="muted">Este item não possui insumos selecionados no PM. Você pode selecioná-lo por completo.</p>}</section>})}</div><div className="actions"><button type="button" className="button primary" disabled={!selectedInputIds.length&&!selectedItemIds.length} onClick={()=>setMaintenancePickerOpen(false)}>Usar {selectedInputIds.length+selectedItemIds.length} seleção(ões)</button></div></>}</section></div>}
      {pendingWholeItem&&<div className="dialog-backdrop nested-dialog" role="presentation" onMouseDown={()=>setPendingWholeItem(undefined)}><section className="whole-item-confirmation" role="dialog" aria-modal="true" aria-labelledby="whole-item-confirmation-title" onMouseDown={event=>event.stopPropagation()}><span className="section-kicker">Selecionar item completo</span><h2 id="whole-item-confirmation-title">Tem certeza de que deseja selecionar o item completo?</h2><p>Nenhum insumo foi selecionado. Será criada uma única NC referente ao item <strong>{pendingWholeItem.description}</strong> por completo.</p><div className="actions"><button type="button" className="button" onClick={()=>setPendingWholeItem(undefined)}>Cancelar</button><button type="button" className="button primary" onClick={()=>{setSelectedItemIds(current=>[...new Set([...current,pendingWholeItem.id])]);setPendingWholeItem(undefined)}}>Selecionar item completo</button></div></section></div>}
      <div className="actions maintenance-editor-actions occurrence-create-actions">
        <button type="button" className="button" onClick={onCancel}>
          Cancelar
        </button>
        <button className="button primary" disabled={saving}>
          {saving ? "Salvando…" : "Salvar rascunho"}
        </button>
      </div>
    </form>
  );
}

function OccurrenceDetail({
  id,
  onBack,
  onError,
}: {
  id: string;
  onBack: () => void;
  onError: (x: string) => void;
}) {
  const [item, setItem] = useState<Occurrence>(),
    [catalogs, setCatalogs] = useState<Record<string, CatalogItem[]>>({}),
    [pendingStatusChange, setPendingStatusChange] = useState<{
      status: string;
      label: string;
    }>(),
    [timelineOpen, setTimelineOpen] = useState(false),
    [statusJustification, setStatusJustification] = useState(""),
    [changingStatus, setChangingStatus] = useState(false),
    [occurrenceDate, setOccurrenceDate] = useState(""),
    [savingOccurrenceDate, setSavingOccurrenceDate] = useState(false),
    [evidenceModalOpen,setEvidenceModalOpen]=useState(false),
    [evidencePreview,setEvidencePreview]=useState<File>(),
    [loadingEvidenceId,setLoadingEvidenceId]=useState<string>(),
    [deletingEvidenceId,setDeletingEvidenceId]=useState<string>(),
    [pendingEvidenceDeletion,setPendingEvidenceDeletion]=useState<OccurrenceEvidence>(),
    [commentsModalOpen,setCommentsModalOpen]=useState(false),
    [triageOpinion,setTriageOpinion]=useState(""),
    [triageOpinionBeforeEdit,setTriageOpinionBeforeEdit]=useState(""),
    [triageOpinionOpen,setTriageOpinionOpen]=useState(false),
    [triageAutosaveStatus,setTriageAutosaveStatus]=useState<"idle"|"saving"|"saved">("idle");
  const commentsEndRef=useRef<HTMLDivElement>(null);
  const load = () =>
    Promise.all([api.occurrence(id), api.occurrenceCatalogs()])
      .then(([i, c]) => {
        setItem(i);
        setOccurrenceDate(i.occurrence_date);
        setTriageOpinion(i.triage_notes||"");
        setCatalogs(c);
      })
      .catch((e) => onError(e.message));
  useEffect(() => {
    void load();
  }, [id]);
  useEffect(()=>{
    if(!commentsModalOpen)return;
    requestAnimationFrame(()=>commentsEndRef.current?.scrollIntoView({block:"end"}));
  },[commentsModalOpen,item?.comments?.length]);
  if (!item) return <div className="card loading">Carregando ocorrência…</div>;
  const submitTriage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await api.triageOccurrence(id, {
        decision: f.get("decision"),
        categoryId: f.get("categoryId") || undefined,
        originId: f.get("originId") || undefined,
        problemSource: f.get("problemSource") || undefined,
        typeId: f.get("typeId") || undefined,
        responsibleDepartmentId: f.get("responsibleDepartmentId") || undefined,
        assignee: f.get("assignee") || undefined,
        notes: f.get("notes"),
      });
      load();
    } catch (e: any) {
      onError(e.message);
    }
  };
  const addComment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form=e.currentTarget;
    const f = new FormData(form);
    try {
      await api.commentOccurrence(id, String(f.get("body")));
      form.reset();
      await load();
    } catch (e: any) {
      onError(e.message);
    }
  };
  const saveTreatment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await api.updateOccurrence(id, {
        qualityAnalysis: f.get("qualityAnalysis"),
        rootCauseId: f.get("rootCauseId") || undefined,
        rootCauseDetail: f.get("rootCauseDetail"),
        solution: f.get("solution"),
        solutionEvidenceText: f.get("solutionEvidenceText"),
        responsibleDepartmentId: f.get("responsibleDepartmentId") || undefined,
        assignee: f.get("assignee"),
        dueDate: f.get("dueDate") || undefined,
      });
      await load();
    } catch (e: any) {
      onError(e.message);
    }
  };
  const changeStatus = (status: string, label: string) => {
    setPendingStatusChange({ status, label });
    setStatusJustification("");
  };
  const saveOccurrenceDate = async () => {
    if (!occurrenceDate || occurrenceDate === item.occurrence_date) return;
    setSavingOccurrenceDate(true);
    try {
      await api.updateOccurrence(id, { occurrenceDate });
      await load();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setSavingOccurrenceDate(false);
    }
  };
  const confirmStatusChange = async () => {
    if (!pendingStatusChange || statusJustification.trim().length < 3) return;
    setChangingStatus(true);
    try {
      await api.changeOccurrenceStatus(
        id,
        pendingStatusChange.status,
        statusJustification.trim(),
      );
      await load();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setChangingStatus(false);
      setPendingStatusChange(undefined);
      setStatusJustification("");
    }
  };
  const printReport = async () => {
    try {
      const report = await api.occurrenceReport(id),
        o = report.occurrence,
        popup = window.open("", "_blank");
      if (!popup) throw new Error("PERMITA POP-UPS PARA IMPRIMIR O RELATÓRIO");
      const rows = [
        ["CÓDIGO", o.code],
        ["DATA DA OCORRÊNCIA", o.occurrence_date],
        ["SETOR NOTIFICADOR", o.notifier_department],
        ["CATEGORIA", o.category_name],
        ["ORIGEM", o.origin_name],
        ["FONTE", o.problem_source],
        ["TIPO", o.type_name],
        ["ASSUNTO", o.title],
        ["FATO", o.fact],
        ["ANÁLISE DA QUALIDADE", o.quality_analysis],
        [
          "CAUSA RAIZ",
          `${o.root_cause_name || ""} ${o.root_cause_detail || ""}`,
        ],
        ["SETOR RESPONSÁVEL", o.responsible_department],
        ["SOLUÇÃO", o.solution],
        ["EVIDÊNCIA DA SOLUÇÃO", o.solution_evidence_text],
        ["STATUS", statusLabel[o.status] || o.status],
        ["AUTOR", o.created_by_name],
        ["RESPONSÁVEL", o.assignee],
        ["OBSERVAÇÕES", o.observations],
      ];
      popup.document.write(
        `<title>${o.code} · Relatório de ocorrência</title><style>body{font:12px Arial;margin:32px;color:#111}header{border-bottom:3px solid #123f68;margin-bottom:20px}h1{font-size:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #bbb;padding:8px;text-align:left;vertical-align:top}th{width:24%;background:#123f68;color:#fff}@media print{body{margin:12mm}}</style><header><b>FLEXIBASE · REGISTRO DA QUALIDADE</b><h1>RELATÓRIO DE OCORRÊNCIA</h1><p>EMITIDO POR ${report.generatedBy}</p></header><table>${rows.map(([a, b]) => `<tr><th>${a}</th><td>${String(b || "NÃO INFORMADO").toLocaleUpperCase("pt-BR")}</td></tr>`).join("")}</table>`,
      );
      popup.document.close();
      popup.print();
    } catch (e: any) {
      onError(e.message);
    }
  };
  const attach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const policyError = attachmentSelectionError([file], (item.evidences as OccurrenceEvidence[] | undefined)?.reduce((sum, evidence) => sum + evidence.size, 0) || 0);
    if (policyError) {
      onError(policyError);
      return;
    }
    const contentBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    try {
      await api.attachOccurrence(id, {
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        contentBase64,
      });
      load();
      e.target.value = "";
    } catch (e: any) {
      onError(e.message);
    }
  };
  const openEvidence=async(evidence:OccurrenceEvidence)=>{
    setLoadingEvidenceId(evidence.id);
    try{
      const blob=await api.occurrenceEvidenceContent(id,evidence.id);
      setEvidencePreview(new File([blob],evidence.file_name,{type:evidence.mime_type}));
    }catch(error:any){onError(error.message)}finally{setLoadingEvidenceId(undefined)}
  };
  const deleteEvidence=async()=>{
    const evidence=pendingEvidenceDeletion;
    if(!evidence)return;
    setDeletingEvidenceId(evidence.id);
    try{
      await api.deleteOccurrenceEvidence(id,evidence.id);
      setEvidencePreview(undefined);
      setPendingEvidenceDeletion(undefined);
      await load();
    }catch(error:any){onError(error.message)}finally{setDeletingEvidenceId(undefined)}
  };
  const openTriageOpinion=()=>{setTriageOpinionBeforeEdit(triageOpinion);setTriageOpinionOpen(true)};
  const cancelTriageOpinion=()=>{setTriageOpinion(triageOpinionBeforeEdit);setTriageOpinionOpen(false)};
  const saveTriageDraft=async(body:Record<string,unknown>)=>{
    setTriageAutosaveStatus("saving");
    try{await api.saveOccurrenceTriageDraft(id,body);setTriageAutosaveStatus("saved")}
    catch(error:any){setTriageAutosaveStatus("idle");onError(error.message)}
  };
  const autosaveTriageField=(target:EventTarget|null)=>{
    if(!(target instanceof HTMLInputElement||target instanceof HTMLSelectElement))return;
    const fieldByName:Record<string,string>={decision:"decision",categoryId:"categoryId",originId:"originId",problemSource:"problemSource",typeId:"typeId",responsibleDepartmentId:"responsibleDepartmentId",assignee:"assignee"};
    const field=fieldByName[target.name];if(!field)return;
    void saveTriageDraft({[field]:target.value||null});
  };
  const applyTriageOpinion=async()=>{await saveTriageDraft({notes:triageOpinion});setTriageOpinionOpen(false)};
  return (
    <div className="occurrence-detail">
      <div className="detail-heading">
        <div className="occurrence-title">
          <span className="mono">{item.code}</span>
          <h2>{item.title}</h2>
          <span className={`status-pill ${tone(item.status)}`}>
            {statusLabel[item.status] || item.status}
          </span>
        </div>
        <div className="occurrence-actions">
          <button className="ghost-link" onClick={onBack}>
            <ArrowLeft />
            Ocorrências
          </button>
          <button className="secondary" onClick={() => void printReport()}>
            Relatório
          </button>
          <RecordTrashAction label={item.title} onDelete={async()=>{try{await api.trashRecord("occurrence",item.id);onBack()}catch(error){onError((error as Error).message);throw error}}}/>
          {item.status === "draft" && (
            <button
              className="primary"
              onClick={async () => {
                try {
                  await api.submitOccurrence(id);
                  load();
                } catch (e: any) {
                  onError(e.message);
                }
              }}
            >
              <Send />
              Enviar para triagem
            </button>
          )}
          {["in_analysis", "reopened"].includes(item.status) && (
            <button
              className="primary"
              onClick={() =>
                void changeStatus("in_treatment", "Iniciar tratamento")
              }
            >
              Iniciar tratamento
            </button>
          )}
          {["in_treatment", "awaiting_evidence"].includes(item.status) && (
            <button
              className="primary"
              onClick={() => void changeStatus("completed", "Concluir")}
            >
              Concluir
            </button>
          )}
          {item.status === "in_treatment" && (
            <button
              className="secondary"
              onClick={() =>
                void changeStatus("awaiting_evidence", "Solicitar evidência")
              }
            >
              Solicitar evidência
            </button>
          )}
          {["completed", "cancelled"].includes(item.status) && (
            <button
              className="secondary"
              onClick={() => void changeStatus("reopened", "Reabrir")}
            >
              Reabrir
            </button>
          )}
          {!["draft", "completed", "cancelled", "not_applicable"].includes(
            item.status,
          ) && (
            <button
              className="danger"
              onClick={() => void changeStatus("cancelled", "Cancelar")}
            >
              Cancelar
            </button>
          )}
        </div>
      </div>
      <div className="detail-grid">
        <section className="card detail-main">
          <h3>Registro da ocorrência</h3>
          <dl className="facts">
            <div>
              <dt>Data</dt>
              <dd className="occurrence-date-editor">
                <DatePicker
                  value={occurrenceDate}
                  onValueChange={setOccurrenceDate}
                  aria-label="Data da ocorrência"
                />
                <button
                  type="button"
                  className="secondary compact"
                  disabled={savingOccurrenceDate || !occurrenceDate || occurrenceDate === item.occurrence_date}
                  onClick={() => void saveOccurrenceDate()}
                >
                  {savingOccurrenceDate ? "Salvando…" : "Salvar"}
                </button>
              </dd>
            </div>
            <div>
              <dt>Autor</dt>
              <dd>{item.created_by_name}</dd>
            </div>
            <div>
              <dt>Setor notificador</dt>
              <dd>{item.notifier_department}</dd>
            </div>
            <div>
              <dt>Categoria</dt>
              <dd>{item.category_name || "Pendente"}</dd>
            </div>
            <div>
              <dt>Origem</dt>
              <dd>{item.origin_name || "Pendente"}</dd>
            </div>
            <div>
              <dt>Fonte</dt>
              <dd>{item.problem_source || "Não preenchido"}</dd>
            </div>
            <div>
              <dt>Tipo</dt>
              <dd>{item.type_name || "Pendente"}</dd>
            </div>
            <div>
              <dt>Responsável</dt>
              <dd>{item.assignee || "Não atribuído"}</dd>
            </div>
          </dl>
          <div className="narrative">
            <h3>Fato e evidência</h3>
            <p>{item.fact}</p>
            {item.initial_evidence && (
              <p className="muted">{item.initial_evidence}</p>
            )}
          </div>
        </section>
        <aside className="detail-side">
          <section className="card">
            <h3>
              <Paperclip />
              Evidências
            </h3>
            <p className="muted">Arquivos e registros relacionados à não conformidade.</p>
            <button type="button" className="button occurrence-evidence-button" onClick={()=>setEvidenceModalOpen(true)}><Eye/>Ver evidências ({item.evidences?.length||0})</button>
          </section>
          <section className="card">
            <h3>
              <MessageSquare />
              Comentários
            </h3>
            <p className="muted">Converse com a equipe sobre o tratamento desta ocorrência.</p>
            <button type="button" className="button occurrence-evidence-button" onClick={()=>setCommentsModalOpen(true)}><MessageSquare/>Abrir conversa ({item.comments?.length||0})</button>
          </section>
        </aside>
      </div>
      {evidenceModalOpen&&<div className="maintenance-attachments-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setEvidenceModalOpen(false)}}>
        <section className="maintenance-attachments-dialog" role="dialog" aria-modal="true" aria-label="Evidências da não conformidade">
          <header><div><span className="section-kicker">Não conformidade</span><h3>Evidências</h3><p>Adicione arquivos e consulte suas pré-visualizações.</p></div><button type="button" aria-label="Fechar" onClick={()=>setEvidenceModalOpen(false)}><X/></button></header>
          <div className="maintenance-attachments-content">
            <label className="document-dropzone occurrence-evidence-dropzone"><input className="dropzone-file-input" type="file" accept={ATTACHMENT_ACCEPT} onChange={attach}/><Paperclip/><div><strong>Adicionar arquivo</strong><small>Compactados, imagens, planilhas, vídeos, áudios ou PDF · até 30 MB na seção</small></div></label>
            {item.evidences?.length?<div className="maintenance-attachment-list">{(item.evidences as OccurrenceEvidence[]).map(e=><article className="maintenance-attachment" key={e.id}><button type="button" className="thumbnail-button" onClick={()=>void openEvidence(e)} aria-label={`Pré-visualizar ${e.file_name}`}><EvidenceThumbnail occurrenceId={id} evidence={e}/></button><div className="attachment-metadata"><button type="button" className="attachment-name" disabled={loadingEvidenceId===e.id} onClick={()=>void openEvidence(e)}>{e.file_name}</button><small>{Math.ceil(e.size/1024)} KB</small></div><div className="attachment-card-actions"><button type="button" className="preview-attachment" disabled={loadingEvidenceId===e.id||deletingEvidenceId===e.id} onClick={()=>void openEvidence(e)} aria-label={`Abrir ${e.file_name}`}><Eye/></button><button type="button" disabled={deletingEvidenceId===e.id} onClick={()=>setPendingEvidenceDeletion(e)} aria-label={`Remover evidência ${e.file_name}`}><Trash2/></button></div></article>)}</div>:<p className="wizard-attachment-empty">Nenhuma evidência adicionada.</p>}
          </div>
          <footer><span>{item.evidences?.length||0} arquivo(s)</span></footer>
        </section>
      </div>}
      {commentsModalOpen&&<div className="occurrence-chat-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setCommentsModalOpen(false)}}><section className="occurrence-chat-dialog" role="dialog" aria-modal="true" aria-labelledby="occurrence-chat-title"><header><div><span className="section-kicker">{item.code}</span><h3 id="occurrence-chat-title">Comentários</h3><p>Conversa da equipe sobre esta não conformidade.</p></div><button type="button" aria-label="Fechar conversa" onClick={()=>setCommentsModalOpen(false)}><X/></button></header><div className="occurrence-chat-messages">{item.comments?.length?item.comments.map(c=><article className="occurrence-chat-message" key={c.id}><div className="occurrence-chat-avatar" aria-hidden="true">{String(c.author_name||"U").trim().charAt(0).toLocaleUpperCase("pt-BR")}</div><div><header><strong>{c.author_name}</strong><time>{new Date(c.created_at).toLocaleString("pt-BR")}</time></header><p>{c.body}</p></div></article>):<div className="occurrence-chat-empty"><MessageSquare/><strong>Nenhuma mensagem ainda</strong><span>Inicie a conversa com a equipe.</span></div>}<div ref={commentsEndRef}/></div><form className="occurrence-chat-composer" onSubmit={addComment}><textarea name="body" required rows={2} placeholder="Escreva uma mensagem…" aria-label="Nova mensagem"/><button type="submit" className="button primary"><Send/>Enviar</button></form></section></div>}
      {pendingEvidenceDeletion&&<div className="dialog-backdrop item-removal-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget&&!deletingEvidenceId)setPendingEvidenceDeletion(undefined)}}><section className="item-removal-dialog" role="dialog" aria-modal="true" aria-labelledby="evidence-removal-title"><header><div><span className="eyebrow">Confirmação</span><h3 id="evidence-removal-title">Excluir evidência?</h3></div><button type="button" disabled={Boolean(deletingEvidenceId)} aria-label="Fechar confirmação" onClick={()=>setPendingEvidenceDeletion(undefined)}><X/></button></header><div className="item-removal-content"><p>O arquivo <strong>{pendingEvidenceDeletion.file_name}</strong> será excluído permanentemente desta não conformidade.</p></div><footer><button type="button" className="button" disabled={Boolean(deletingEvidenceId)} onClick={()=>setPendingEvidenceDeletion(undefined)}>Cancelar</button><button type="button" className="button danger" disabled={Boolean(deletingEvidenceId)} onClick={()=>void deleteEvidence()}><Trash2/>{deletingEvidenceId?"Excluindo…":"Excluir evidência"}</button></footer></section></div>}
      {evidencePreview&&<div className="wizard-preview-backdrop" role="presentation" onMouseDown={()=>setEvidencePreview(undefined)}><section className="wizard-preview-modal" role="dialog" aria-modal="true" aria-label={`Prévia de ${evidencePreview.name}`} onMouseDown={event=>event.stopPropagation()}><header><div><span className="section-kicker">Pré-visualização</span><h3>{evidencePreview.name}</h3><small>{Math.ceil(evidencePreview.size/1024)} KB</small></div><button type="button" aria-label="Fechar pré-visualização" onClick={()=>setEvidencePreview(undefined)}><X/></button></header><div className="wizard-preview-content"><AttachmentFullPreview file={evidencePreview}/></div></section></div>}
      {["awaiting_triage", "reopened"].includes(item.status) && (
        <form className="card triage-card" onSubmit={submitTriage} onBlurCapture={event=>autosaveTriageField(event.target)}>
          <div>
            <p className="section-kicker">Qualidade</p>
            <h2>Triagem e classificação</h2>
            <p>Valide a procedência, classifique e defina o responsável.</p>
            <small className="triage-autosave-status" aria-live="polite">{triageAutosaveStatus==="saving"?"Salvando…":triageAutosaveStatus==="saved"?"Alterações salvas automaticamente":"Salvamento automático ao sair do campo"}</small>
          </div>
          <div className="form-grid triage-fields">
            <Field label="Decisão">
              <select name="decision" defaultValue={item.triage_decision||"proceed"}>
                <option value="proceed">Procedente</option>
                <option value="partially_proceed">
                  Parcialmente procedente
                </option>
                <option value="needs_information">
                  Solicitar mais informações
                </option>
                <option value="not_applicable">Não procedente</option>
                <option value="duplicate">Duplicada</option>
              </select>
            </Field>
            <Field label="Categoria">
              <CatalogSelect
                name="categoryId"
                items={catalogs.category}
                value={item.category_id}
              />
            </Field>
            <Field label="Origem">
              <CatalogSelect
                name="originId"
                items={catalogs.origin}
                value={item.origin_id}
              />
            </Field>
            <Field label="Fonte">
              <UppercaseInput name="problemSource" defaultValue={item.problem_source || ""} maxLength={500} placeholder="Informe a causa do problema" />
            </Field>
            <Field label="Tipo">
              <CatalogSelect
                name="typeId"
                items={catalogs.type}
                value={item.type_id}
              />
            </Field>
            <Field label="Setor responsável">
              <CatalogSelect
                name="responsibleDepartmentId"
                items={catalogs.department}
                value={item.responsible_department_id}
              />
            </Field>
            <Field label="Responsável">
              <input name="assignee" defaultValue={item.assignee||""} placeholder="Nome ou e-mail" />
            </Field>
            <Field label="Parecer da triagem" full>
              <input type="hidden" name="notes" value={triageOpinion}/>
              <button type="button" className={`triage-opinion-trigger${triageOpinion?" has-value":""}`} onClick={openTriageOpinion}>{triageOpinion?<><FileText/><span><strong>Parecer registrado</strong><small>Visualizar ou editar</small></span></>:<span>Adicionar parecer da triagem</span>}<Maximize2/></button>
            </Field>
          </div>
          <div className="actions">
            <button
              type="button"
              className="button"
              onClick={() => setTimelineOpen(true)}
            >
              Linha do tempo
            </button>
            <button className="primary">
              <ShieldCheck />
              Concluir triagem
            </button>
          </div>
        </form>
      )}
      {triageOpinionOpen&&<div className="triage-opinion-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)cancelTriageOpinion()}}><section className="triage-opinion-dialog" role="dialog" aria-modal="true" aria-labelledby="triage-opinion-title"><header><div><span className="section-kicker">Triagem e classificação</span><h3 id="triage-opinion-title">Parecer da triagem</h3><p>Edite o parecer com a formatação aplicada diretamente ao texto.</p></div><button type="button" aria-label="Fechar editor" onClick={cancelTriageOpinion}><X/></button></header><RichMarkdownEditor value={triageOpinion} onChange={setTriageOpinion}/><footer><span>{triageAutosaveStatus==="saving"?"Salvando…":`${triageOpinion.length} caracteres`}</span><div><button type="button" className="button" onClick={cancelTriageOpinion}>Cancelar</button><button type="button" className="button primary" disabled={triageOpinion.trim().length<3||triageAutosaveStatus==="saving"} onClick={()=>void applyTriageOpinion()}>Aplicar parecer</button></div></footer></section></div>}
      {![
        "draft",
        "awaiting_triage",
        "needs_information",
        "not_applicable",
        "cancelled",
        "reopened",
      ].includes(item.status) && (
        <form className="card occurrence-treatment" onSubmit={saveTreatment}>
          <div>
            <p className="section-kicker">Tratamento</p>
            <h2>Análise, causa e solução</h2>
            <p>Registre o tratamento formal antes de avançar o fluxo.</p>
          </div>
          <div className="form-grid occurrence-treatment-grid">
            <Field label="Setor responsável">
              <CatalogSelect
                name="responsibleDepartmentId"
                items={catalogs.department}
                value={item.responsible_department_id}
              />
            </Field>
            <Field label="Responsável">
              <UppercaseInput
                name="assignee"
                defaultValue={item.assignee || ""}
              />
            </Field>
            <Field label="Prazo">
              <DatePicker
                name="dueDate"
                defaultValue={item.due_date || ""}
                aria-label="Prazo"
              />
            </Field>
            <Field label="Causa raiz">
              <CatalogSelect
                name="rootCauseId"
                items={catalogs.cause}
                value={item.root_cause_id}
              />
            </Field>
            <Field label="Análise da Qualidade" full>
              <UppercaseTextarea
                name="qualityAnalysis"
                defaultValue={item.quality_analysis || ""}
              />
            </Field>
            <Field label="Descrição detalhada da causa" full>
              <UppercaseTextarea
                name="rootCauseDetail"
                defaultValue={item.root_cause_detail || ""}
              />
            </Field>
            <Field label="Solução adotada" full>
              <UppercaseTextarea
                name="solution"
                defaultValue={item.solution || ""}
              />
            </Field>
            <Field label="Evidência ou justificativa" full>
              <UppercaseTextarea
                name="solutionEvidenceText"
                defaultValue={item.solution_evidence_text || ""}
              />
            </Field>
          </div>
          <div className="actions">
            <button className="primary">Salvar tratamento</button>
          </div>
        </form>
      )}
      {timelineOpen && <TimelineDialog title={item.code} description="Histórico de alterações e movimentações da ocorrência." events={(item.timeline||[]).map(event=>({id:event.id,title:statusLabel[event.to_status]||event.to_status,detail:event.reason,author:event.changed_by_name,at:event.created_at,color:timelineColor(event.to_status)}))} emptyMessage="Nenhuma movimentação registrada nesta ocorrência." onClose={()=>setTimelineOpen(false)}/>}
      {pendingStatusChange && (
        <div
          className="dialog-backdrop"
          role="presentation"
          onMouseDown={() => !changingStatus && setPendingStatusChange(undefined)}
        >
          <section
            className="kanban-status-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="occurrence-status-dialog-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <span className="section-kicker">Alterar status</span>
            <h2 id="occurrence-status-dialog-title">
              {pendingStatusChange.label}
            </h2>
            <p>
              Informe a justificativa da alteração. Ela será registrada na
              linha do tempo da não conformidade.
            </p>
            <label>
              <span>Justificativa</span>
              <textarea
                autoFocus
                value={statusJustification}
                onChange={(event) => setStatusJustification(event.target.value)}
                placeholder="Descreva o motivo da alteração"
              />
            </label>
            <div className="actions">
              <button
                type="button"
                className="button"
                disabled={changingStatus}
                onClick={() => setPendingStatusChange(undefined)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="button primary"
                disabled={changingStatus || statusJustification.trim().length < 3}
                onClick={() => void confirmStatusChange()}
              >
                {changingStatus ? "Salvando…" : "Confirmar alteração"}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
function CatalogSelect({
  name,
  items = [],
  placeholder = "Selecione",
  required,
  value,
}: {
  name: string;
  items?: CatalogItem[];
  placeholder?: string;
  required?: boolean;
  value?: string;
}) {
  return (
    <select name={name} required={required} defaultValue={value || ""}>
      <option value="">{placeholder}</option>
      {items.map((i) => (
        <option key={i.id} value={i.id}>
          {i.name}
        </option>
      ))}
    </select>
  );
}
function Field({
  label,
  full,
  className,
  children,
}: {
  label: string;
  full?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={[full?"full":"",className||""].filter(Boolean).join(" ")}>
      <span>{label}</span>
      {children}
    </label>
  );
}
